import { filter } from '@atlaskit/adf-utils/traverse';

import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { DEFAULT_MAX_PAGE_SIZE } from './utils/pprConsts.ts';
import DetailsSummaryADFQuery, {
	type useDetailsSummaryADFQueryQuery$data,
	type useDetailsSummaryADFQueryQuery$variables,
} from './hooks/__generated__/useDetailsSummaryADFQueryQuery.graphql.ts';

const EXTENSION_TYPES = new Set(['extension', 'bodiedExtension', 'inlineExtension']);

interface DetailsSummaryExtensionNode {
	type: string;
	attrs?: {
		extensionKey?: string;
		parameters?: {
			macroParams?: Record<string, { value: string }>;
			macroMetadata?: {
				macroId?: { value: string };
			};
		};
	};
}

const findDetailsSummaryExtensions = (adf: any): DetailsSummaryExtensionNode[] => {
	return filter(adf, (node) => {
		return EXTENSION_TYPES.has(node.type) && node.attrs?.extensionKey === 'detailssummary';
	}) as DetailsSummaryExtensionNode[];
};

const buildVariables = (
	node: DetailsSummaryExtensionNode,
	contentId: string,
	spaceKey: string,
): useDetailsSummaryADFQueryQuery$variables => {
	const macroParams = node.attrs?.parameters?.macroParams;
	const macroId = node.attrs?.parameters?.macroMetadata?.macroId?.value || '';

	return {
		contentId: macroParams?.['_parentId']?.value || contentId,
		cql: macroParams?.cql?.value,
		detailsId: macroParams?.id?.value,
		headings: macroParams?.headings?.value,
		sortBy: macroParams?.sortBy?.value,
		spaceKey,
		countLikes: macroParams?.showLikesCount?.value === 'true',
		reverseSort: macroParams?.reverseSort?.value === 'true',
		countComments: macroParams?.showCommentsCount?.value === 'true',
		countUnresolvedComments: macroParams?.showUnresolvedCommentsCount?.value === 'true',
		showPageLabels: macroParams?.showPageLabels?.value === 'true',
		showLastModified: macroParams?.showLastModified?.value === 'true',
		showCreator: macroParams?.showCreator?.value === 'true',
		pageIndex: 0,
		macroId,
		pageSize: Math.min(parseInt(macroParams?.pageSize?.value || '30'), DEFAULT_MAX_PAGE_SIZE),
	};
};

export const preloadDetailsSummary = async ({
	contentId,
	spaceKey,
	contentNodes,
}: {
	contentId: string;
	spaceKey: string;
	contentNodes: any;
}) => {
	if (!expValEquals('confluence_ttvc_macro_detailssummary', 'isEnabled', true)) {
		return;
	}

	const resolvedNodes =
		contentNodes && typeof contentNodes.then === 'function' ? await contentNodes : contentNodes;

	if (!resolvedNodes?.[0]?.body?.dynamic?.value) {
		return;
	}

	if (resolvedNodes[0].body.dynamic.representation !== 'atlas_doc_format') {
		return;
	}

	let documentADF;
	try {
		documentADF = JSON.parse(resolvedNodes[0].body.dynamic.value);
	} catch {
		return;
	}

	const extensions = findDetailsSummaryExtensions(documentADF);
	if (extensions.length === 0) {
		return;
	}

	// eslint-disable-next-line check-preloaders/no-promise-all
	await Promise.all(
		extensions.map((extensionNode) => {
			const variables = buildVariables(extensionNode, contentId, spaceKey);
			return query<useDetailsSummaryADFQueryQuery$data, useDetailsSummaryADFQueryQuery$variables>({
				query: DetailsSummaryADFQuery,
				variables,
				context: { single: true },
			});
		}),
	);
};
